import { useLazyQuery, useMutation } from '@apollo/client'

import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'

import { Avatar, IconButton } from '@material-ui/core'
import { ProductCardAutosuggestQuery } from 'api/graphQL/queries/product.queries'
import { workspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    ListingCardAutosuggest,
    ProductCardAutosuggestResponse,
} from 'api/graphQL/types/product.type'
import { AddProductTypeInput } from 'models/addProductTypeInput'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import { NormalizedDocumentTemplateViewItem } from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import React from 'react'
import { useParams } from 'react-router-dom'
import AddProductTypeDialog from './AddProductTypeDialog'

const useAddProductTypeStyles = makeStyles((theme: Theme) => ({
    AddProductType: {
        display: 'flex',
        height: theme.spacing(5),
        width: theme.spacing(4.4),
        position: 'relative',
        backgroundColor: '#F87D42',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(0.5),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    addPTCircle: {
        backgroundColor: '#F78909',
        height: 'inherit',
        padding: theme.spacing(0.38, 0.63),
        '& .MuiIconButton-root': {
            padding: 0,
        },
    },
    avatar: {
        backgroundColor: theme.palette.common.white,
        color: '#F78909',
        height: theme.spacing(3.15),
        width: theme.spacing(3.15),
        fontSize: pxToRem(20),
    },
    hide: {
        display: 'none',
    },
}))

interface AddProductTypeProps {
    template?: NormalizedDocumentTemplateViewItem
    parent?: NormalizedDocumentTemplateViewItem
    resetInput?: () => void
}

const AddProductType: React.FC<AddProductTypeProps> = ({
    template,
    parent,
    resetInput,
}) => {
    const classes = useAddProductTypeStyles()
    const { projectProductFamilyId } = workspaceContents()
    const { projectId } = useParams<{ projectId?: string }>()
    const { addNewProductTypeIntoTree } = React.useContext(AreaContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        showAddProductTypePrompt,
        setShowAddProductTypePrompt,
    ] = React.useState<boolean>(false)

    const [
        getListingCardAutosuggest,
        { loading: autosuggestCardLoading, data: autosuggestCardData },
    ] = useLazyQuery<ProductCardAutosuggestResponse>(
        ProductCardAutosuggestQuery,
        {
            displayName: 'ProductCardAutoSuggestResult',
            fetchPolicy: 'no-cache',
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on product autosuggest in dialog',
                        variant: 'error',
                        id: `ProductListingsDialog_ProductAutosuggestError`,
                    },
                })
            },
        }
    )

    const handlePromptDisplay = React.useCallback(() => {
        resetInput!()
        setShowAddProductTypePrompt((past) => !past)
    }, [setShowAddProductTypePrompt, showAddProductTypePrompt, template])
    const handleInputChange = React.useCallback(
        (val) => {
            getListingCardAutosuggest({
                variables: {
                    input: {
                        autoSuggestField: 'PRODUCTTYPE',
                        nodeIds: null,
                        searchText: val,
                        projectId: projectId!,
                        limit: 50,
                        skip: 0,
                        partnerships: [],
                        isPremiumProprietary: false,
                    },
                },
            })
        },
        [
            setShowAddProductTypePrompt,
            getListingCardAutosuggest,
            projectId,
            template,
        ]
    )

    const handleOKPrompt = React.useCallback(
        (val: Partial<ListingCardAutosuggest>, newText: string) => {
            const addProductTypeInput: AddProductTypeInput = {
                projectId: projectId ?? '',
                projectProductFamilyId: projectProductFamilyId!,
                newProductTypeName: newText ?? '',
                productTypeNuggetId: '',
                destinationParentContentItemId: parent?.contentItem?.id,
                index: template?.paragraphOrder,
            }
            if (parent?.partType != 'Part') {
                const index = (parent?.children || []).findIndex(
                    (x) => x.id === template?.id
                )
                if (index > 0) {
                    const siblingTemplate = parent?.children[index - 1]
                    addProductTypeInput.index =
                        siblingTemplate?.paragraphOrder! + 1
                }
            }

            if (!!val) {
                addProductTypeInput.newProductTypeName = val.productType!
                addProductTypeInput.productTypeNuggetId = val.pTElementId!
            }

            addNewProductTypeIntoTree?.(addProductTypeInput)
            handlePromptDisplay()
        },
        [handlePromptDisplay, template, addNewProductTypeIntoTree, parent]
    )

    return (
        <div className={classes.AddProductType}>
            <IconButton
                aria-label="add-newPT"
                data-testid="addNewPTButton"
                onMouseDown={handlePromptDisplay}
            >
                <Avatar aria-label="addPT" className={classes.avatar}>
                    P
                </Avatar>
            </IconButton>
            {showAddProductTypePrompt && (
                <AddProductTypeDialog
                    openDialog={showAddProductTypePrompt}
                    handleCancel={handlePromptDisplay}
                    handleOK={handleOKPrompt}
                    dialogHeading="Add Product Type"
                    dropDownLabel="Enter a Product Type Name"
                    handleSearchInput={handleInputChange}
                    loadingOptions={autosuggestCardLoading}
                    options={
                        autosuggestCardData?.listingCardsAutoSuggest
                            ?.productFamilyToProductTypes ?? []
                    }
                    okText="Confirm"
                />
            )}
        </div>
    )
}

export default AddProductType
